import React, { useEffect, useState } from 'react';
import { BombiixModal } from '../BombiixModal';
import { NewInput } from '../PIM/common';
import Loader from '../Utility/Loader/Loader';

export interface ConfirmationModalProps {
  show: boolean;
  title: string;
  /** Body content. A node (rather than a plain string) lets callers add chips, lists or emphasis below the text. */
  message: React.ReactNode;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
  confirmDisabled?: boolean;
  /** Label on the confirm button. Defaults to the modal `title`. */
  confirmLabel?: string;
  /** Confirm-button fill: `red` for destructive actions (default), `green` for a non-destructive continue. */
  confirmVariant?: 'red' | 'green';
  /**
   * Require the user to type this word before confirming. Use for actions that destroy
   * something a click cannot undo. Omitted, the confirm button is live immediately.
   *
   * The match is exact against the upper-cased phrase: no trimming, no case folding. The prompt
   * shows the word in capitals, so "Delete " (different case, trailing space) must not arm the
   * button — BM-9855 QA found it did, and deleted a batch.
   */
  typedPhrase?: string;
  /** Overrides the default "Type PHRASE to continue" helper line. */
  typedHelperText?: string;
}

/**
 * Confirmation popup on the Bombiix master-design modal chrome (`BombiixModal`):
 * header action bar with a centered title and red ✕ close, a centered message,
 * and a single confirm button — red for destructive flows, green for a
 * non-destructive continue. TypeScript replacement for
 * `ModalPopup/ConfirmationPopup.js` for the delete-confirm flow.
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  show,
  title,
  message,
  onConfirm,
  onClose,
  loading = false,
  confirmDisabled = false,
  confirmLabel,
  confirmVariant = 'red',
  typedPhrase,
  typedHelperText,
}) => {
  const [typed, setTyped] = useState('');
  const expected = typedPhrase?.toUpperCase() ?? '';

  /* Clear the box each time the popup opens, so a previous confirmation cannot carry over. */
  useEffect(() => {
    if (show) {
      setTyped('');
    }
  }, [show]);

  const phraseSatisfied = !typedPhrase || typed === expected;

  return (
    <BombiixModal show={show} title={title} onClose={onClose} hideSave size="md" backdrop="static">
      {/* No vertical padding of its own: `.bombiix-modal__body` already pads 12px all round, and
          stacking another 16px on top left a visible band of dead space under the confirm button. */}
      <div className="d-flex flex-column align-items-center" style={{ gap: 16 }}>
        {/* A div, not a p: `message` is a node and may carry block-level content, which a <p> cannot legally nest. */}
        <div className="text-center">{message}</div>

        {typedPhrase && (
          <div className="w-100 px-3">
            <p className="text-center mb-1">
              {typedHelperText ?? <>Type <strong>{expected}</strong> to continue</>}
            </p>
            {/* NewInput, not a bare <input>: the master-design field carries the border,
                height and focus ring, so this popup needs no styling of its own. */}
            <NewInput
              id="bombiix-typed-confirm"
              value={typed}
              placeholder={expected}
              autoComplete="off"
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        )}

        {loading ? (
          <Loader />
        ) : (
          <button
            type="button"
            className={`bombiix-btn btn-${confirmVariant}`}
            onClick={onConfirm}
            disabled={confirmDisabled || !phraseSatisfied}
            aria-label="Confirm"
          >
            {confirmLabel ?? title}
          </button>
        )}
      </div>
    </BombiixModal>
  );
};

export default ConfirmationModal;
